import React, { useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Modal,
  Row,
  Col,
  message,
  Progress,
  List,
  Alert,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Workspace } from '../../types/workspaces';
import { getWorkspaces, addWorkspace, updateWorkspace, deleteWorkspaceCompletely } from '../../api/workspaces';
import WorkspaceModal from '../../components/modal/WorkspaceModal';
import { getCustomers, updateCustomer } from '../../api/customers';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const { Title } = Typography;

const Workspaces: React.FC = () => {
  const { t } = useTranslation(['workspaces', 'common']);
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState(0);
  const [deletionStatus, setDeletionStatus] = useState<string>('');
  const [deletionResult, setDeletionResult] = useState<{
    success: boolean;
    deletedCounts: Record<string, number>;
    errors: string[];
  } | null>(null);

  // Fetch workspaces
  const { data: workspacesData, isLoading: workspacesLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => getWorkspaces(),
    select: data => data.workspaces,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => getCustomers(),
    select: data => data.customers,
    enabled: isModalVisible,
    staleTime: 5 * 60 * 1000,
  });

  const workspaces = workspacesData || [];
  const customers = customersData || [];

  // Mutations
  const addMutation = useMutation({
    mutationFn: async (workspaceData: Omit<Workspace, 'id'>) => {
      const workspaceId = await addWorkspace(workspaceData);
      await updateCustomer(workspaceData.customer_id, {
        workspace_id: workspaceId,
        workspace_name: workspaceData.name,
        workspace_slug: workspaceData.slug,
      });
      return workspaceId;
    },
    onSuccess: () => {
      message.success(t('workspaces:success.create'));
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setIsModalVisible(false);
      setEditingWorkspace(null);
    },
    onError: () => message.error(t('workspaces:errors.saveFailed')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<Workspace, 'id'> }) => updateWorkspace(id, data),
    onSuccess: () => {
      message.success(t('workspaces:success.update'));
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setIsModalVisible(false);
      setEditingWorkspace(null);
    },
    onError: () => message.error(t('workspaces:errors.saveFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setIsDeleting(true);
      setDeletionProgress(0);
      setDeletionStatus('Starting workspace deletion...');
      setDeletionResult(null);
      
      try {
        const result = await deleteWorkspaceCompletely(id, (progress: number, status: string) => {
          setDeletionProgress(progress);
          setDeletionStatus(status);
        });
        setDeletionResult(result);
        return result;
      } finally {
        setIsDeleting(false);
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        message.success(t('workspaces:success.delete'));
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      } else {
        message.warning('Workspace deleted with some errors. Check console for details.');
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      }
    },
    onError: (error: any) => {
      message.error(error.message || t('workspaces:errors.deleteFailed'));
    },
  });

  const columns = [
    {
      title: t('workspaces:table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Workspace) => (
        <Link to={`/workspaces/${record.id}`} state={{ workspace: record }}>{name}</Link>
      ),
    },
    {
      title: t('workspaces:table.customer'),
      dataIndex: 'customer_id',
      key: 'customer',
      render: (customer_id: string) => {
        const customer = customers.find(c => c.id === customer_id);
        return customer ? <Link to={`/customers/${customer.id}`}>{customer.property_name || customer.id}</Link> : '-';
      },
    },
    {
      title: t('workspaces:table.presidentName'),
      dataIndex: 'presidentName',
      key: 'presidentName',
    },
    {
      title: t('workspaces:table.language'),
      dataIndex: 'language',
      key: 'language',
    },
    {
      title: t('workspaces:table.currency'),
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: t('workspaces:table.totalUnits'),
      dataIndex: ['stats', 'total_units'],
      key: 'total_units',
    },
    {
      title: t('workspaces:table.totalUsers'),
      dataIndex: ['stats', 'total_users'],
      key: 'total_users',
    },
    {
      title: t('common:actions'),
      key: 'actions',
      render: (_: any, record: Workspace) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common:edit')}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {t('common:delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const handleAdd = useCallback(() => {
    setEditingWorkspace(null);
    setIsModalVisible(true);
  }, []);

  const handleEdit = useCallback((workspace: Workspace) => {
    setEditingWorkspace(workspace);
    setIsModalVisible(true);
  }, []);

  const handleDelete = useCallback((workspace: Workspace) => {
    Modal.confirm({
      title: 'Delete Workspace Completely',
      content: (
        <div>
          <Alert
            message="Warning: This action cannot be undone!"
            description={
              <div>
                <p>This will permanently delete the workspace <strong>"{workspace.name}"</strong> and ALL related data including:</p>
                <ul style={{ marginTop: 8, marginBottom: 8 }}>
                  <li>All apartment units, blocks, and property data</li>
                  <li>All residents, employees, and users</li>
                  <li>All maintenance requests, incidents, and tasks</li>
                  <li>All financial records (payables, receivables, payrolls)</li>
                  <li>All communications (chat threads, notifications, news)</li>
                  <li>All other workspace-related data across 30+ collections</li>
                </ul>
                <p><strong>This is a comprehensive deletion that will remove thousands of records.</strong></p>
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>Are you absolutely sure you want to proceed?</p>
        </div>
      ),
      okText: 'Delete Everything',
      okType: 'danger',
      cancelText: t('common:cancel'),
      width: 600,
      onOk: () => deleteMutation.mutate(workspace.id),
    });
  }, [deleteMutation, t]);

  const handleSave = useCallback((workspaceData: Omit<Workspace, 'id'>) => {
    console.log('handleSave called with data:', workspaceData);
    console.log('Language:', workspaceData.language);
    console.log('Currency:', workspaceData.currency);
    
    if (editingWorkspace) {
      updateMutation.mutate({ id: editingWorkspace.id, data: workspaceData });
    } else {
      addMutation.mutate(workspaceData);
    }
  }, [editingWorkspace, addMutation, updateMutation]);

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>{t('workspaces:title')}</Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                {t('workspaces:add')}
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={workspaces}
            rowKey={record => record.id}
            loading={workspacesLoading || addMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
            pagination={false}
          />
        </Space>
      </Card>

      <WorkspaceModal
        open={isModalVisible}
        onSave={handleSave}
        onCancel={() => setIsModalVisible(false)}
        workspace={editingWorkspace}
      />

      {/* Deletion Progress Modal */}
      <Modal
        title="Deleting Workspace"
        open={isDeleting}
        footer={null}
        closable={false}
        width={600}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" style={{ marginBottom: 16 }} />
          <Typography.Title level={4} style={{ marginBottom: 16 }}>
            Deleting Workspace and All Related Data
          </Typography.Title>
          <Progress 
            percent={deletionProgress} 
            status="active"
            style={{ marginBottom: 16 }}
          />
          <Typography.Text type="secondary">
            {deletionStatus}
          </Typography.Text>
          <Alert
            message="Please do not close this window during deletion"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      </Modal>

      {/* Deletion Result Modal */}
      <Modal
        title="Deletion Complete"
        open={!!deletionResult && !isDeleting}
        onOk={() => setDeletionResult(null)}
        onCancel={() => setDeletionResult(null)}
        width={700}
      >
        {deletionResult && (
          <div>
            <Alert
              message={deletionResult.success ? "Deletion Successful" : "Deletion Completed with Errors"}
              description={deletionResult.success 
                ? "All workspace data has been successfully deleted."
                : "Workspace was deleted but some errors occurred during the process."
              }
              type={deletionResult.success ? "success" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Typography.Title level={5}>Deletion Summary:</Typography.Title>
            <List
              size="small"
              dataSource={Object.entries(deletionResult.deletedCounts).filter(([_, count]) => count > 0)}
              renderItem={([collection, count]) => (
                <List.Item>
                  <Typography.Text strong>{collection}:</Typography.Text>
                  <Typography.Text>{count} documents deleted</Typography.Text>
                </List.Item>
              )}
              style={{ marginBottom: 16 }}
            />

            {deletionResult.errors.length > 0 && (
              <div>
                <Typography.Title level={5} type="danger">Errors:</Typography.Title>
                <List
                  size="small"
                  dataSource={deletionResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Typography.Text type="danger">{error}</Typography.Text>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Workspaces; 